import { ReactElement, useCallback, useEffect, useMemo, useRef } from "react";
import { RowSelectedEvent, RowClickedEvent } from "ag-grid-community";
import { useHistory, useLocation } from "react-router-dom";
import { AgGrid, Grid, AgGridReact, Flex } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { ComplianceCaseStatus } from "~/graphql/supergraphEnums";
import { LoadingCellRenderer } from "~/pages/Compliance/Common/LoadingCellRenderer";
import NoRowsOverlay from "~/pages/Compliance/Common/NoRowsOverlay";
import { CaseStatusRenderer } from "~/pages/Compliance/ComplianceTable/CaseStatusRenderer";
import { DateRenderer } from "~/pages/Compliance/ComplianceTable/DateRenderer";
import { ResolutionRenderer } from "~/pages/Compliance/ComplianceTable/ResolutionRenderer";
import { statusValueFormatter } from "~/pages/Compliance/ComplianceTable/utils";
import { useComplianceEntityOnboardingEnabled } from "../../flags";
import { dashboardContainer } from "../../styles_vanilla.css";
import { INTERNAL_ESCALATION_TYPE_LABEL } from "../../utils/constants";
import { AffectedPositionsRenderer } from "../AffectedPositionsRenderer";
import { EscalationsGraphqlDataSource } from "../escalationsGraphqlDataSource";
import { ImpactRenderer } from "../ImpactRenderer";
import { OriginCaseRenderer } from "../OriginCaseRenderer";
import { AGGridCellRenderParams, EscalationRow } from "../types";
import * as styles from "./style_vanilla.css";

const useSearchParams = (locationSearch: string) => {
  return useMemo(() => {
    return new URLSearchParams(locationSearch);
  }, [locationSearch]);
};

const useEscalationFilters = (searchParams: URLSearchParams, pathname: string) => {
  const filtersKey = "escalationFilters";

  useEffect(() => {
    const sourceCaseId = searchParams.get("sourceCaseId");
    const clientId = searchParams.get("clientId");
    const escalationIds = searchParams.get("escalationIds");

    const isMainEscalationsRoute = pathname === "/compliance/escalations";

    if (isMainEscalationsRoute && (sourceCaseId || clientId || escalationIds)) {
      const searchString = searchParams.toString();
      if (searchString) {
        sessionStorage.setItem(filtersKey, `?${searchString}`);
      }
    } else if (isMainEscalationsRoute && !sourceCaseId && !clientId && !escalationIds) {
      sessionStorage.removeItem(filtersKey);
    }
  }, [searchParams, pathname]);

  useEffect(() => {
    return () => {
      if (!pathname.startsWith("/compliance/escalations")) {
        sessionStorage.removeItem(filtersKey);
      }
    };
  }, [pathname]);
};

export const ComplianceEscalationsTableContainer = (): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const savedFiltersRef = useRef<any>();

  const searchParams = useSearchParams(location.search);
  useEscalationFilters(searchParams, location.pathname);

  const handleRowSelectionChanged = useCallback((event: RowSelectedEvent) => {
    const panelCandidate = event.api.getStatusPanel("agSelectedRowCountComponent");
    type SelectedCountPanel = {
      setValue: (v: string) => void;
      setDisplayed: (b: boolean) => void;
    };
    const panel = panelCandidate as unknown as SelectedCountPanel;
    const selectionState =
      event.api.getServerSideSelectionState() as AgGrid.IServerSideSelectionState;
    const idsAreSelectedVsUnselected: boolean = !selectionState.selectAll;
    const toggledNodeCount: number = selectionState.toggledNodes.length;
    const selectedRowCount: number = idsAreSelectedVsUnselected
      ? toggledNodeCount
      : event.api.paginationGetRowCount() - toggledNodeCount;
    if (
      panel &&
      typeof panel.setValue === "function" &&
      typeof panel.setDisplayed === "function"
    ) {
      panel.setValue(selectedRowCount.toString());
      panel.setDisplayed(selectedRowCount !== 0);
    }
  }, []);

  const { sourceCaseId, clientId, escalationIds } = useMemo(() => {
    const escalationIdsParam = searchParams.get("escalationIds");
    const escalationIdsList = escalationIdsParam ? escalationIdsParam.split(",") : null;
    return {
      sourceCaseId: searchParams.get("sourceCaseId"),
      clientId: searchParams.get("clientId"),
      escalationIds: escalationIdsList,
    };
  }, [searchParams]);

  const dataSource = useMemo(() => {
    return new EscalationsGraphqlDataSource({ sourceCaseId, clientId, escalationIds });
  }, [sourceCaseId, clientId, escalationIds]);

  const handleRowClicked = useCallback(
    (rowData: RowClickedEvent<EscalationRow>) => {
      if (rowData.data?.caseId) {
        history.push({
          pathname: `${globalEnv.complianceRoute}/escalations/${rowData.data.caseId}`,
          search: location.search,
        });
      }
    },
    [history, location.search],
  );

  const onGridReady = useCallback((event: AgGrid.GridReadyEvent<EscalationRow>) => {
    if (savedFiltersRef.current) {
      event.api.setFilterModel(savedFiltersRef.current);
    }
  }, []);

  const onFilterChanged = useCallback((event: AgGrid.FilterChangedEvent) => {
    savedFiltersRef.current = event.api.getFilterModel();
  }, []);

  const initialFilter = useCallback(
    (event: AgGrid.FirstDataRenderedEvent) => {
      const hasQuery = Boolean(sourceCaseId || clientId || escalationIds?.length);
      if (!hasQuery && !savedFiltersRef.current) {
        event.api.getFilterInstance("status")?.setModel({
          values: Object.values(ComplianceCaseStatus).filter(
            (status) => status !== ComplianceCaseStatus.Closed,
          ),
        });
        event.api.onFilterChanged();
      }
    },
    [sourceCaseId, clientId, escalationIds],
  );

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = useMemo(
    () => ({
      autoSizeStrategy: { type: "fitGridWidth" },
      rowHeight: 61,
      suppressCellFocus: true,
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      statusBar: {
        statusPanels: [
          { statusPanel: "agTotalRowCountComponent" },
          { statusPanel: "agSelectedRowCountComponent" },
        ],
      },
      rowModelType: "serverSide",
      serverSideDatasource: dataSource,
      getRowId: (params: { data: EscalationRow }) => params.data.escalationId.toString(),
      onGridReady: onGridReady,
      onFilterChanged: onFilterChanged,
      onFirstDataRendered: initialFilter,
    }),
    [dataSource, initialFilter, onGridReady, onFilterChanged],
  );

  const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = useMemo(
    () => [
      {
        field: "escalationId",
        headerName: "Escalation ID",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        sortable: true,
        cellStyle: styles.centerCellStyle,
      },
      ...(internalEscalationsEnabled
        ? [
            {
              field: "type",
              headerName: "Type",
              sortable: false,
              filter: false,
              cellStyle: styles.centerCellStyle,
            },
          ]
        : []),
      {
        field: "client",
        headerName: "Client",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        sortable: true,
        cellStyle: styles.centerCellStyle,
      },
      {
        field: "profile",
        headerName: "Profile",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        sortable: true,
        cellStyle: styles.centerCellStyle,
      },
      {
        field: "originCase",
        headerName: "Origin Case",
        cellRenderer: (params: AGGridCellRenderParams) => (
          <OriginCaseRenderer params={params as any} />
        ),
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        sortable: true,
        cellStyle: styles.centerCellStyle,
      },
      {
        field: "impact",
        headerName: "Impact",
        cellRenderer: (params: AGGridCellRenderParams) => <ImpactRenderer params={params} />,
        sortable: false,
        filter: false,
        cellStyle: styles.centerCellStyle,
      },
      {
        field: "creationDate",
        headerName: "Creation date",
        cellRenderer: (params: AGGridCellRenderParams) => <DateRenderer params={params} />,
        sortable: true,
        cellStyle: styles.dateCellStyle,
      },
      {
        field: "affectedPositions",
        headerName: "Affected Positions",
        cellRenderer: (params: AGGridCellRenderParams) => (
          <AffectedPositionsRenderer params={params as any} />
        ),
        sortable: false,
        filter: false,
        cellStyle: styles.centerCellStyle,
      },
      {
        field: "status",
        headerName: "Status",
        filter: "agSetColumnFilter",
        filterParams: {
          values: Object.values(ComplianceCaseStatus),
          valueFormatter: statusValueFormatter,
        },
        cellRenderer: (params: { value: string }) => (
          <CaseStatusRenderer params={{ value: params.value }} showIcon />
        ),
        sortable: true,
        cellStyle: styles.centerCellStyle,
      },
      {
        field: "resolution",
        headerName: "Resolution",
        cellRenderer: (params: { value: string; data?: EscalationRow }) => (
          <ResolutionRenderer
            resolution={params.value}
            internal={params.data?.type === INTERNAL_ESCALATION_TYPE_LABEL}
          />
        ),
        sortable: false,
        cellStyle: styles.centerCellStyle,
      },
      {
        field: "assignee",
        headerName: "Assignee",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains"],
          maxNumConditions: 1,
        },
        sortable: true,
        cellStyle: styles.centerCellStyle,
      },
    ],
    [internalEscalationsEnabled],
  );

  return (
    <Flex className={dashboardContainer}>
      <Grid<EscalationRow>
        gridOptions={gridOptions}
        columnDefs={columnDefs}
        onRowClicked={handleRowClicked}
        suppressColumnVirtualisation={true}
        suppressRowVirtualisation={true}
        onSelectionChanged={handleRowSelectionChanged}
        loadingCellRenderer={LoadingCellRenderer}
        noRowsOverlayComponent={NoRowsOverlay}
        noRowsOverlayComponentParams={{ message: "No escalations found" }}
        pagination
        paginationPageSizeSelector={false}
        paginationPageSize={100}
        cacheBlockSize={100}
      />
    </Flex>
  );
};

export default ComplianceEscalationsTableContainer;
